import { Box, Button, Divider, Hidden, HStack, Pressable, SearchIcon, Spacer, Stack, Text, useMediaQuery, VStack } from 'native-base';
import React, { memo, useContext, useEffect, useState } from 'react';
import { CameraOutlineIcon } from '../../../assets';
import { NewLoanInProgressConstants } from '../../../constants';
import Functions from '../../../utils/Functions';
import { UserDetailsContext } from '../../../containers/NewLoanInProgress';
import { useUploadFileMutation } from '../../../hooks';
import IconButton from '../../IconButton';
import ToastMessage from '../ToastMessage';
import DragDropFileAndBrowse from '../../DragDropFileAndBrowse';
import MessageRow from '../../MessageRow';
const UploadBox = _ref => {
  let {
    trackingItemName,
    sectionIndex,
    detail,
    logUserEvent,
    infoText,
    color
  } = _ref;
  const [selectedFile, setSelectedFile] = useState(null);
  const [alertDetails, setAlertDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const {
    url,
    authToken,
    loanNumber,
    logApiEvent,
    onBrowse,
    onTakePhoto,
    setInvalidateCache,
    onPreview
  } = useContext(UserDetailsContext);
  const {
    mutate,
    isLoading
  } = useUploadFileMutation({
    url: url,
    accessToken: authToken,
    loanNumber: loanNumber,
    logApiEvent: logApiEvent,
    setAlertDetails,
    setSelectedFile,
    logUserEvent,
    setInvalidateCache
  });
  const checkIfFileTypeInvalid = type => {
    const validType = ['PDF', 'JPEG', 'JPG', 'PNG'];
    return !validType.includes(type);
  };
  useEffect(() => {
    logUserEvent({
      action: 'NLIPUploadBoxDisplayed',
      type: 'NewLoanInProgress',
      description: `${trackingItemName} - ${sectionIndex === 1 ? 'Document Status' : 'Your Tasks'}`
    });
  }, [logUserEvent, sectionIndex, trackingItemName]);
  const validateFile = file => {
    setAlertDetails(null);
    logUserEvent({
      action: 'ValidateDocumentStarted',
      type: 'NewLoanInProgress'
    });
    if (file) {
      const size = file.size / 1000000;
      if (size > 8) {
        logUserEvent({
          action: 'ValidateDocumentSizeError',
          type: 'NewLoanInProgress'
        });
        setAlertDetails({
          toastType: 'ERROR',
          message: NewLoanInProgressConstants.UPLOAD_DOCUMENTS_SIZE_ERROR
        });
      } else if (checkIfFileTypeInvalid(Functions.getFileType(file.name))) {
        logUserEvent({
          action: 'ValidateDocumentTypeError',
          type: 'NewLoanInProgress',
          description: `FileName: ${file.name}`
        });
        setAlertDetails({
          toastType: 'ERROR',
          message: NewLoanInProgressConstants.UPLOAD_DOCUMENTS_TYPE_ERROR
        });
      } else {
        logUserEvent({
          action: 'ValidateDocumentSelectedSuccessfully',
          type: 'NewLoanInProgress'
        });
        setSelectedFile(file);
      }
    }
    logUserEvent({
      action: 'ValidateDocumentEnd',
      type: 'NewLoanInProgress'
    });
  };
  const handleMobileOnBrowse = async () => {
    logUserEvent({
      action: 'BrowseMobileSelectionStarted',
      type: 'NewLoanInProgress'
    });
    setAlertDetails(null);
    try {
      if (onBrowse) {
        setLoading(true);
        let fileDetails = await onBrowse();
        logUserEvent({
          action: 'BrowseMobileFileSelected',
          type: 'NewLoanInProgress'
        });
        validateFile(fileDetails);
      }
    } finally {
      logUserEvent({
        action: 'MobileDocumentFileSelectionCompleted',
        type: 'NewLoanInProgress'
      });
      setLoading(false);
    }
    logUserEvent({
      action: 'BrowseMobileSelectionEnd',
      type: 'NewLoanInProgress'
    });
  };
  const handleMobileOnPhoto = async () => {
    logUserEvent({
      action: 'TakeAPhotoSessionStarted',
      type: 'NewLoanInProgress'
    });
    try {
      setLoading(true);
      if (onTakePhoto) {
        let fileDetails = await onTakePhoto();
        logUserEvent({
          action: 'TakeAPhotoCompleted',
          type: 'NewLoanInProgress'
        });
        validateFile(fileDetails);
      }
    } finally {
      setLoading(false);
      logUserEvent({
        action: 'TakeAPhotoSessionEnd',
        type: 'NewLoanInProgress'
      });
    }
  };
  const onFileUpload = async () => {
    logUserEvent({
      action: 'FileUploadProcessStarted',
      type: 'NewLoanInProgress'
    });
    mutate({
      file: selectedFile,
      documentName: trackingItemName
    });
  };
  const [isSmallScreen] = useMediaQuery({
    maxWidth: 768
  });
  return /*#__PURE__*/React.createElement(VStack, {
    flex: "1",
    space: "4",
    ml: {
      base: sectionIndex === 0 ? 3 : 0,
      md: sectionIndex === 0 ? 20 : 16,
      lg: sectionIndex === 0 ? 20 : 16
    },
    mt: {
      base: 2,
      md: 0,
      lg: 0
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: "sm",
    fontWeight: "normal",
    fontFamily: "body",
    color: "black.700",
    accessibilityLabel: detail,
    testID: detail,
    display: {
      base: 'flex',
      lg: 'none',
      md: 'none'
    }
  }, detail), isSmallScreen && infoText ? /*#__PURE__*/React.createElement(MessageRow, {
    text: infoText,
    color: color,
    opacity: 100,
    maxW: '85%'
  }) : null, /*#__PURE__*/React.createElement(VStack, null, !selectedFile || selectedFile.name === '' ? /*#__PURE__*/React.createElement(Box, {
    borderRadius: "4",
    backgroundColor: {
      lg: '#F1F4F7'
    },
    justifyContent: "center",
    alignItems: "center",
    paddingY: 6
  }, /*#__PURE__*/React.createElement(Hidden, {
    platform: ['web']
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IconButton, {
    Icon: /*#__PURE__*/React.createElement(SearchIcon, {
      size: 6,
      color: "#ffffff"
    }),
    title: "browse",
    accessibilityLabel: 'browse file to upload',
    accessibilityRole: "button",
    testID: 'Browse_DocumentUpload_Mobile',
    isLoading: loading,
    onPress: handleMobileOnBrowse,
    width: "100%"
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "freedomOutline",
    onPress: handleMobileOnPhoto,
    my: 5,
    accessibilityLabel: 'take photo to upload',
    accessibilityRole: "button",
    testID: 'TakePhoto_DocumentUploadBox_Mobile',
    isLoading: loading,
    width: "100%"
  }, /*#__PURE__*/React.createElement(HStack, {
    space: 3,
    flex: 1,
    justifyContent: "center",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(CameraOutlineIcon, {
    height: 28,
    width: 28,
    color: "#0a4a8d"
  }), /*#__PURE__*/React.createElement(Text, {
    fontSize: "lg",
    fontWeight: "bold",
    fontFamily: "body",
    lineHeight: "md",
    color: "blue.400",
    textTransform: "uppercase"
  }, "TAKE PHOTO"))))), /*#__PURE__*/React.createElement(Hidden, {
    till: "lg",
    platform: ['ios', 'android']
  }, /*#__PURE__*/React.createElement(DragDropFileAndBrowse, {
    validateFile: validateFile,
    showDragDrop: true
  })), /*#__PURE__*/React.createElement(Hidden, {
    from: "lg",
    platform: ['ios', 'android']
  }, /*#__PURE__*/React.createElement(DragDropFileAndBrowse, {
    validateFile: validateFile,
    showDragDrop: false
  }))) : /*#__PURE__*/React.createElement(Stack, {
    flex: 1,
    flexDirection: {
      base: 'column',
      md: 'row',
      lg: 'row',
      xl: 'row'
    },
    justifyContent: {
      base: 'center',
      md: 'flex-start',
      lg: 'flex-start'
    },
    alignItems: {
      base: 'center',
      md: 'flex-start',
      lg: 'flex-start'
    }
    // mt={{ lg: 6, md: 6 }}
    // ml={{ lg: 20, md: 20, base: 0 }}
    ,
    space: {
      md: 0,
      lg: 0,
      base: 2
    }
  }, /*#__PURE__*/React.createElement(VStack, {
    space: 2,
    mx: {
      base: 3,
      md: 0,
      lg: 0
    },
    flex: 1
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: "sm",
    fontWeight: "medium",
    fontFamily: "body",
    lineHeight: "md",
    color: "text.running",
    accessibilityLabel: selectedFile.name,
    accessibilityRole: "text",
    testID: selectedFile.name
  }, selectedFile.name), /*#__PURE__*/React.createElement(HStack, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: "sm",
    fontWeight: "normal",
    fontFamily: "body",
    lineHeight: "md",
    color: "text.running",
    accessibilityLabel: Functions.getFileSize(selectedFile.size),
    accessibilityRole: "text",
    testID: Functions.getFileSize(selectedFile.size),
    mr: 8
  }, Functions.getFileSize(selectedFile.size)), /*#__PURE__*/React.createElement(HStack, {
    space: 4,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Pressable, {
    onPress: () => onPreview(selectedFile),
    accessibilityLabel: `preview`,
    accessibilityRole: "button",
    testID: `preview ${selectedFile.name}`,
    disabled: isLoading
  }, /*#__PURE__*/React.createElement(Text, {
    color: isLoading ? 'black.700' : 'blue.400',
    fontSize: "sm",
    fontWeight: "medium",
    fontFamily: "body",
    lineHeight: "md",
    accessibilityLabel: `preview text`,
    accessibilityRole: "text",
    testID: `preview ${selectedFile.name} text`
  }, "Preview")), /*#__PURE__*/React.createElement(Divider, {
    orientation: "vertical",
    h: "3",
    bg: "primary.400"
  }), /*#__PURE__*/React.createElement(Pressable, {
    onPress: () => {
      setSelectedFile(null);
      setAlertDetails(null);
    },
    accessibilityLabel: `remove`,
    accessibilityRole: "button",
    testID: `remove ${selectedFile.name}`,
    disabled: isLoading
  }, /*#__PURE__*/React.createElement(Text, {
    color: isLoading ? 'black.700' : 'blue.400',
    fontSize: "sm",
    fontWeight: "medium",
    fontFamily: "body",
    lineHeight: "md",
    accessibilityLabel: `remove text`,
    accessibilityRole: "text",
    testID: `remove ${selectedFile.name} text`
  }, "Remove"))))), /*#__PURE__*/React.createElement(Spacer, null), /*#__PURE__*/React.createElement(Hidden, {
    till: "md"
  }, /*#__PURE__*/React.createElement(IconButton, {
    title: "submit",
    accessibilityLabel: `submit`,
    accessibilityRole: "button",
    testID: `upload ${selectedFile.name}`,
    isLoading: isLoading,
    onPress: onFileUpload,
    w: {
      base: 'full',
      md: 202,
      lg: 202
    }
  }))), selectedFile && selectedFile.name ? /*#__PURE__*/React.createElement(Hidden, {
    from: 'md'
  }, /*#__PURE__*/React.createElement(IconButton, {
    mt: -3,
    alignSelf: "center",
    title: "submit",
    accessibilityLabel: `submit`,
    accessibilityRole: "button",
    testID: `upload ${selectedFile.name}`,
    isLoading: isLoading,
    onPress: onFileUpload,
    w: {
      base: 'full',
      md: 202,
      lg: 202
    }
  })) : null, /*#__PURE__*/React.createElement(Box, {
    ml: {
      base: 4,
      md: 20,
      lg: 20
    },
    mt: {
      base: 2,
      md: 0,
      lg: 0
    }
  }, /*#__PURE__*/React.createElement(VStack, {
    space: 3
  }, alertDetails && /*#__PURE__*/React.createElement(ToastMessage, {
    toastType: alertDetails === null || alertDetails === void 0 ? void 0 : alertDetails.toastType,
    message: alertDetails === null || alertDetails === void 0 ? void 0 : alertDetails.message
  })))));
};
export default /*#__PURE__*/memo(UploadBox);
//# sourceMappingURL=index.js.map